import React from 'react';
import FormField from '../molecules/FormField';
import Input from '../atoms/Input';
import Select from '../atoms/Select';
import RoleMultiSelect from '../molecules/RoleMultiSelect';
import TrashIcon from '../atoms/TrashIcon';

const BOOLEAN_OPTIONS = ['Yes', 'No'];
const Star = () => <span style={{ color: '#dc2626' }} aria-hidden="true"> *</span>;

export default function TableCard({ name, level, value, initialFormValues, setFormValues, onChange, selected = false, onSelect, onDelete, subtitle, roleList = [], showRoleError = false, showEscalationError = false, userList = [], fieldErrors = {} }) {
  const data = value ?? initialFormValues ?? {};
  const role = Array.isArray(data.role) ? data.role : [];
  const userValues = Array.isArray(data.users) ? data.users : [];
  // The toggle only decides which dropdown is shown; both selections are kept
  const view = data.workflowType === 'user' ? 'user' : 'role';
  const roleOptions = roleList.map((item) => ({
    value: item.id ?? item.roleId ?? item.value,
    label: item.name ?? item.label,
  }));
    const userLabel = (user) => {
    // Prefer an existing full name; otherwise combine firstName + lastName (skip empty/null lastName)
    const combined = [user?.firstName, user?.lastName]
      .map((part) => String(part ?? '').trim())
      .filter(Boolean)
      .join(' ');
    const userName = String(user?.name ?? user?.userName ?? user?.label ?? combined ?? '').trim() || combined;
    const roleName = String(
      user?.roleName
        ?? user?.role?.name
        ?? (typeof user?.role === 'string' ? user.role : '')
        ?? user?.roles?.[0]?.name
        ?? '',
    ).trim();
    return roleName ? `${userName} (${roleName})` : userName;
  };
  const userOptions = userList.map((user) => ({
    value: user.id ?? user.userId ?? user.value,
    label: userLabel(user),
  }));
  const updateField = (field, nextValue) => {
    const nextData = { ...data, [field]: nextValue };
    if (setFormValues) {
      setFormValues(nextData);
    } else {
      onChange?.(nextData);
    }
  };
  const nothingSelected = role.length === 0 && userValues.length === 0;
  const peopleError = (showRoleError || Boolean(fieldErrors.role)) && nothingSelected;
  const invalid = (flag) => (flag ? 'ca-control--invalid' : '');
  const accent = level === 1 ? 'green' : level === 2 ? 'orange' : 'blue';
  const fieldId = (field) => `workflow-level-${level}-${field}`;
  const subtitleText =
    subtitle ||
    (level === 1
      ? 'Initial Handler'
      : level === 2
        ? 'Escalation if L1 TAT exceeded'
        : 'Escalation workflow');
  const toggle = [
    { value: 'role', label: 'Role', count: role.length },
    { value: 'user', label: 'User', count: userValues.length },
  ];

  return (
    <article
      className={`ca-table-card ca-table-card--${accent}${selected ? ' ca-table-card--selected' : ''}`}
      onClick={onSelect}
    >
      <header className="ca-table-card__header">
        <span className="ca-table-card__level">L{level}</span>
        <div className="ca-table-card__header-text">
          <h3 className="ca-table-card__title">{name}</h3>
          <p className="ca-table-card__subtitle">{subtitleText}</p>
        </div>
        <button
          type="button"
          className="ca-table-card__delete"
          aria-label={`Delete ${name}`}
          title={`Delete ${name}`}
          onClick={(event) => {
            event.stopPropagation();
            onDelete?.();
          }}
        >
          <TrashIcon size={15} color="currentColor" />
        </button>
      </header>
      <div className="ca-table-card__divider" />
      <div className="ca-table-card__fields">
        <div className="ca-type-toggle" role="group" aria-label="Workflow type">
          {toggle.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`ca-type-toggle__btn${view === option.value ? ' ca-type-toggle__btn--active' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                if (view !== option.value) updateField('workflowType', option.value);
              }}
            >
              {option.label}{option.count ? ` (${option.count})` : ''}
            </button>
          ))}
        </div>

        {view === 'role' ? (
          <FormField label={<>Select Roles<Star /></>} htmlFor={fieldId('roleIds')}>
            <RoleMultiSelect
              key="role"
              id={fieldId('role')}
              value={role}
              options={roleOptions}
              placeholder="Select role"
              emptyText="No roles available"
              openDirection="down"
              showAll={false}
              maxVisible={3}
              hasError={peopleError}
              onChange={(next) => updateField('role', next)}
            />
          </FormField>
        ) : (
          <FormField label={<>Select Users<Star /></>} htmlFor={fieldId('userIds')}>
            <RoleMultiSelect
              key="user"
              id={fieldId('user')}
              value={userValues}
              options={userOptions}
              placeholder="Select user"
              emptyText="No users available"
              openDirection="down"
              showAll={false}
              maxVisible={3}
              hasError={peopleError}
              onChange={(next) => updateField('users', next)}
            />
          </FormField>
        )}

        <div className="ca-table-card__divider" />

        <FormField label={<>Can Resolve {<>(Change the Status)</>}{/*<Star />*/}</>} htmlFor={fieldId('canResolve')}>
          <Select id={fieldId('canResolve')} className={invalid(fieldErrors.canResolve)} value={data.canResolve} options={BOOLEAN_OPTIONS} onChange={(event) => updateField('canResolve', event.target.value)} />
        </FormField>
        <FormField label={<>Can Reassign {<>(Change the Level)</>}{/*<Star />*/}</>} htmlFor={fieldId('canReassign')}>
          <Select id={fieldId('canReassign')} className={invalid(fieldErrors.canReassign)} value={data.canReassign} options={BOOLEAN_OPTIONS} onChange={(event) => updateField('canReassign', event.target.value)} />
        </FormField>
        <FormField label={<>Final Level{<>(can be resolved or closed the complaint)</>}{/*<Star />*/}</>} htmlFor={fieldId('finalLevel')}>
          <Select id={fieldId('finalLevel')} className={invalid(fieldErrors.finalLevel)} value={data.finalLevel} options={BOOLEAN_OPTIONS} onChange={(event) => updateField('finalLevel', event.target.value)} />
        </FormField>
        <FormField label={<>Escalation time{/*<Star />*/}</>} htmlFor={fieldId('escalationTime')}>
          <div className="ca-table-card__time-input">
            <Input id={fieldId('escalationTime')} type="text" className={invalid(showEscalationError || fieldErrors.escalation)} value={data.escalateAfterMinutes ?? ''} placeholder="--" onChange={(event) => updateField('escalateAfterMinutes', event.target.value)} />
            {/* <span>minutes</span> */}
          </div>
        </FormField>
      </div>
    </article>
  );
}